import type { Company } from '@/types/company.types'
import { useCompanyDashboard } from '../hooks/useCompanies'
import { AttentionPanel } from './company-detail/AttentionPanel'
import { CompanyIdentityBand } from './company-detail/CompanyIdentityBand'
import { CompanyRail } from './company-detail/CompanyRail'
import { GettingStartedPanel } from './company-detail/GettingStartedPanel'
import { MemberRosterPanel } from './company-detail/MemberRosterPanel'
import { TodayPanel } from './company-detail/TodayPanel'
import { WeekPanel } from './company-detail/WeekPanel'

export interface CompanyOverviewTabProps {
  company: Company
}

/**
 * Command layout: one decision column, one reference rail.
 *
 * The column fills its grid track — capping the child while the track claimed
 * the full remainder is what left a dead channel between the two columns. The
 * reading-measure constraint belongs on individual prose blocks, not here.
 */
export function CompanyOverviewTab({ company }: CompanyOverviewTabProps): React.ReactElement {
  const { data: dashboard, isLoading, error } = useCompanyDashboard(company._id)
  const summary = dashboard?.summary

  // One BE definition: attended / (people due by now, not on leave); null when nobody was expected.
  const attendancePct = summary?.attendancePct ?? null

  const weekAttendance = dashboard?.weekAttendance ?? []
  const memberCount = summary?.memberCount ?? company.memberCount ?? 0

  // Chosen from the data, not a flag: a chart of nothing and a row of zeros
  // read as broken on a new company, so until there is history to show the
  // page tells the operator what to do instead.
  const hasHistory =
    weekAttendance.some((d) => d.presentPct !== null) ||
    (summary?.shiftsScheduledWeek ?? 0) > 0 ||
    (summary?.totalWorkedHoursWeek ?? 0) > 0

  return (
    <div className="grid grid-cols-1 items-start gap-6 xl:grid-cols-[minmax(0,1fr)_300px]">
      <div className="min-w-0 space-y-4">
        <CompanyIdentityBand
          company={company}
          summary={summary}
          attendancePct={attendancePct}
          isLoading={isLoading}
          hasError={Boolean(error)}
        />

        <AttentionPanel companyId={company._id} summary={summary} isLoading={isLoading} />

        <TodayPanel summary={summary} isLoading={isLoading} />

        <MemberRosterPanel
          companyId={company._id}
          members={dashboard?.members ?? []}
          memberCount={memberCount}
          timezone={dashboard?.timezone ?? ''}
          isLoading={isLoading}
        />

        {!isLoading && !hasHistory ? (
          <GettingStartedPanel
            companyId={company._id}
            memberCount={memberCount}
            shiftsScheduledWeek={summary?.shiftsScheduledWeek ?? 0}
          />
        ) : (
          <WeekPanel
            companyId={company._id}
            summary={summary}
            weekAttendance={weekAttendance}
            isLoading={isLoading}
          />
        )}
      </div>

      <CompanyRail
        companyId={company._id}
        recentActivity={dashboard?.recentActivity ?? []}
        isLoading={isLoading}
      />
    </div>
  )
}

CompanyOverviewTab.displayName = 'CompanyOverviewTab'
